import { Button, Link, PasswordInput, TextInput } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useTenant } from "@contexts/Tenant/Tenant.context";
import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { useLoginRedirect } from "@hooks/useLoginRedirect";
import { ArrowRightMd } from "@icons";
import { ROUTES } from "@router/router.constants";
import { ERROR_CATEGORIES } from "@services/apiEcms/apiEcms.constants";
import { usePostPublicLogin } from "@services/apiEcms/usePostPublicLogin";
import { formatCaughtError } from "@utils/error.utils";
import { emailRegex } from "@utils/form";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./Login.module.css";
import type { LoginForm } from "./Login.types";

export const Login: FC = () => {
  const { t } = useCustomTranslation(["main", "common"]);
  const { subscriptionUrl } = useTenant();
  const navigate = useNavigate();

  const { isError, error, isPending, mutate } = usePostPublicLogin();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    mode: FORM_VALIDATION_MODE,
  });

  // We need to handle this error as a success for the user, while waiting for the reset mail
  const isChangingPassword =
    isError &&
    formatCaughtError(error).data.type ===
      ERROR_CATEGORIES.CHANGE_INITIAL_PASSWORD;

  useEffect(() => {
    if (isChangingPassword) {
      navigate(ROUTES.LOGIN_CONFIRM);
    }
  }, [navigate, isChangingPassword]);

  useLoginRedirect();

  return (
    <div className={classes.container}>
      <form
        className={classes.form}
        onSubmit={handleSubmit((data) => {
          mutate(data);
        })}
      >
        <h2 className={classNames("font-title-xs", classes.subtitle)}>
          {t("auth.form.subtitle.loginForm")}
        </h2>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              data-testid="test-auth-login-email"
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("form.email.label", { ns: "common" })}
              placeholder={t("form.email.placeholder", { ns: "common" })}
            />
          )}
          rules={{
            required: "form.error.required",
            pattern: { value: emailRegex, message: "form.email.hint" },
          }}
        />
        <Controller
          control={control}
          name="password"
          render={({ field, fieldState }) => (
            <PasswordInput
              {...field}
              data-testid="test-auth-login-password"
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("auth.form.password.label")}
              placeholder={t("auth.form.password.placeholder")}
            />
          )}
          rules={{ required: "form.error.required" }}
        />
        <Button
          className={classes.submitButton}
          data-testid="AuthFormTemplate-login-submit"
          disabled={Boolean(errors.password || errors.email)}
          endIcon={ArrowRightMd}
          isLoading={isPending}
          type="submit"
        >
          {t("auth.form.buttons.connect")}
        </Button>
      </form>
      <Button
        data-testid="AuthFormTemplate-auth-login-cancel"
        onClick={() => navigate(ROUTES.LOGIN_RESET)}
        variant="tertiary"
      >
        {t("auth.form.buttons.forgotPwd")}
      </Button>
      {Boolean(subscriptionUrl) && (
        <Link className={classes.link} href={subscriptionUrl} target="_blank">
          {t("auth.form.subscriptionLink")}
        </Link>
      )}
    </div>
  );
};
